import React from 'react';
import {
    Divider,
    Grid,
    NumberInput,
    Select,
    Stack,
    Switch,
    Text,
    Textarea,
    TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";

const slugify = (text) =>
    text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")   // saca acentos
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

function RafflesForm({ form, loading, isEdit }) {
    const handleTitleChange = (event) => {
        const value = event.currentTarget.value;
        form.setFieldValue("title", value);

        // El slug se autocompleta solo mientras el sorteo no exista todavía.
        // Cambiarlo después rompe los links ya publicados.
        if (!isEdit) form.setFieldValue("slug", slugify(value));
    };

    return (
        <Stack gap={16}>
            <Divider label="General" labelPosition="left" />

            <Grid>
                <Grid.Col span={8}>
                    <TextInput
                        label="Nombre interno"
                        description="No se muestra al público"
                        required
                        disabled={loading}
                        value={form.values.title}
                        onChange={handleTitleChange}
                        error={form.errors.title}
                    />
                </Grid.Col>

                <Grid.Col span={4}>
                    <Select
                        label="Estado"
                        data={[
                            { value: "DRAFT", label: "Borrador" },
                            { value: "ACTIVE", label: "Activo" },
                            { value: "FINISHED", label: "Finalizado" },
                        ]}
                        disabled={loading}
                        {...form.getInputProps("status")}
                    />
                </Grid.Col>

                <Grid.Col span={12}>
                    <TextInput
                        label="Slug"
                        description={`La URL queda /sorteo/${form.values.slug || "..."}`}
                        required
                        disabled={loading || isEdit}
                        {...form.getInputProps("slug")}
                    />
                    {isEdit && (
                        <Text size="xs" c="dimmed" mt={4}>
                            El slug no se edita: cambiarlo rompe los enlaces ya publicados.
                        </Text>
                    )}
                </Grid.Col>

                <Grid.Col span={4}>
                    <DateInput
                        label="Inicio"
                        clearable
                        valueFormat="DD/MM/YYYY"
                        disabled={loading}
                        {...form.getInputProps("startsAt")}
                    />
                </Grid.Col>

                <Grid.Col span={4}>
                    <DateInput
                        label="Cierre"
                        clearable
                        valueFormat="DD/MM/YYYY"
                        disabled={loading}
                        {...form.getInputProps("endsAt")}
                    />
                </Grid.Col>

                <Grid.Col span={4}>
                    <Switch
                        mt={28}
                        label="Sorteo destacado"
                        description="Es el que responde en /sorteo sin slug"
                        disabled={loading}
                        checked={form.values.isFeatured}
                        onChange={(e) =>
                            form.setFieldValue("isFeatured", e.currentTarget.checked)
                        }
                    />
                </Grid.Col>
            </Grid>

            <Divider label="Textos de la página" labelPosition="left" />

            <Grid>
                <Grid.Col span={6}>
                    <TextInput
                        label="Título"
                        placeholder="Tenemos"
                        disabled={loading}
                        {...form.getInputProps("content.heading")}
                    />
                </Grid.Col>

                <Grid.Col span={6}>
                    <TextInput
                        label="Título destacado"
                        description="Se muestra con el degradado"
                        placeholder="sorteo"
                        disabled={loading}
                        {...form.getInputProps("content.highlight")}
                    />
                </Grid.Col>

                <Grid.Col span={12}>
                    <Textarea
                        label="Descripción"
                        autosize
                        minRows={2}
                        disabled={loading}
                        {...form.getInputProps("content.description")}
                    />
                </Grid.Col>

                <Grid.Col span={12}>
                    <Textarea
                        label="Descripción secundaria"
                        autosize
                        minRows={2}
                        disabled={loading}
                        {...form.getInputProps("content.secondaryDescription")}
                    />
                </Grid.Col>

                <Grid.Col span={12}>
                    <TextInput
                        label="Texto del botón"
                        disabled={loading}
                        {...form.getInputProps("content.buttonLabel")}
                    />
                </Grid.Col>
            </Grid>

            <Divider label="Mensajes de confirmación" labelPosition="left" />

            <Grid>
                <Grid.Col span={12}>
                    <TextInput
                        label="Título del mensaje de éxito"
                        disabled={loading}
                        {...form.getInputProps("content.successTitle")}
                    />
                </Grid.Col>

                <Grid.Col span={6}>
                    <Textarea
                        label="Si acierta la pregunta"
                        autosize
                        minRows={2}
                        disabled={loading}
                        {...form.getInputProps("content.successMessageCorrect")}
                    />
                </Grid.Col>

                <Grid.Col span={6}>
                    <Textarea
                        label="Si no acierta"
                        autosize
                        minRows={2}
                        disabled={loading}
                        {...form.getInputProps("content.successMessageWrong")}
                    />
                </Grid.Col>
            </Grid>

            <Divider label="Formulario" labelPosition="left" />

            <Grid>
                <Grid.Col span={6}>
                    <Switch
                        label="Pedir especialidades de interés"
                        disabled={loading}
                        checked={form.values.form.showSpecialties}
                        onChange={(e) =>
                            form.setFieldValue("form.showSpecialties", e.currentTarget.checked)
                        }
                    />
                </Grid.Col>

                <Grid.Col span={6}>
                    <Switch
                        label="Preguntar si ya es alumno"
                        disabled={loading}
                        checked={form.values.form.showAlreadyStudent}
                        onChange={(e) =>
                            form.setFieldValue("form.showAlreadyStudent", e.currentTarget.checked)
                        }
                    />
                </Grid.Col>
            </Grid>

            <Divider label="Pregunta" labelPosition="left" />

            <Grid>
                <Grid.Col span={12}>
                    <Switch
                        label="Incluir pregunta"
                        description="Se toma una al azar de la colección de preguntas"
                        disabled={loading}
                        checked={form.values.question.enabled}
                        onChange={(e) =>
                            form.setFieldValue("question.enabled", e.currentTarget.checked)
                        }
                    />
                </Grid.Col>

                {form.values.question.enabled && (
                    <>
                        <Grid.Col span={8}>
                            <TextInput
                                label="Consigna"
                                placeholder="¿Te animás a responder? Si acertás, sumás doble chance"
                                disabled={loading}
                                {...form.getInputProps("content.questionPrompt")}
                            />
                        </Grid.Col>

                        <Grid.Col span={4}>
                            <NumberInput
                                label="Chances si acierta"
                                min={1}
                                max={10}
                                disabled={loading}
                                {...form.getInputProps("question.chancesIfCorrect")}
                            />
                        </Grid.Col>
                    </>
                )}
            </Grid>

            <Divider label="Leads" labelPosition="left" />

            <Grid>
                <Grid.Col span={4}>
                    <TextInput
                        label="Source"
                        description="Define el tipo de contacto en el CRM"
                        disabled={loading}
                        {...form.getInputProps("lead.source")}
                    />
                </Grid.Col>

                <Grid.Col span={4}>
                    <TextInput
                        label="Flow (n8n)"
                        disabled={loading}
                        {...form.getInputProps("lead.flow")}
                    />
                </Grid.Col>

                <Grid.Col span={4}>
                    <Switch
                        mt={28}
                        label="Enviar email al participar"
                        disabled={loading}
                        checked={form.values.lead.sendEmail}
                        onChange={(e) =>
                            form.setFieldValue("lead.sendEmail", e.currentTarget.checked)
                        }
                    />
                </Grid.Col>
            </Grid>
        </Stack>
    );
}

export default RafflesForm;